import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { Box, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

export const SeeNotesDialog = ({
  seeNotesDialogOpen,
  setSeeNotesDialogOpen,
  defaultText,
  onSubmitNotes,
}: {
  seeNotesDialogOpen: boolean;
  setSeeNotesDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  defaultText: string | null;
  onSubmitNotes?: (newNotes: string) => Promise<void>;
}) => {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [editText, setEditText] = React.useState(defaultText ?? "");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const initialDefaultTextRef = React.useRef(defaultText ?? "");

  const prevOpenRef = React.useRef(false);
  React.useEffect(() => {
    const justOpened = seeNotesDialogOpen && !prevOpenRef.current;
    prevOpenRef.current = seeNotesDialogOpen;
    if (justOpened) {
      const text = defaultText ?? "";
      initialDefaultTextRef.current = text;
      setEditText(text);
      setIsEditMode(false);
    }
  }, [seeNotesDialogOpen, defaultText]);

  const hasUnsavedChanges =
    isEditMode && editText !== initialDefaultTextRef.current;

  const handleClose = () => {
    setSeeNotesDialogOpen(false);
  };

  const handleCancel = () => {
    setSeeNotesDialogOpen(false);
  };

  const handleSubmitNotes = async () => {
    if (!onSubmitNotes) return;
    setIsSubmitting(true);
    try {
      await onSubmitNotes(editText);
      setSeeNotesDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showCloseButton = !hasUnsavedChanges;

  const showEditInHeader = onSubmitNotes && !isEditMode;

  return (
    <Dialog open={seeNotesDialogOpen} fullWidth maxWidth="xs">
      <DialogTitle id="alert-dialog-title">
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontFamily: "Mulish",
          }}
        >
          Notes
          {showEditInHeader && (
            <OrangeTextButton
              aria-label="edit notes"
              text={<EditIcon fontSize="small" />}
              onClick={() => setIsEditMode(true)}
              disabled={false}
            />
          )}
        </Box>
      </DialogTitle>
      {showCloseButton && (
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <DialogContent>
        {isEditMode && onSubmitNotes ? (
          <TextField
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            variant="outlined"
            multiline
            rows={6}
            fullWidth
            sx={{ fontFamily: "Mulish" }}
          />
        ) : (
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {defaultText ?? ""}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {isEditMode && onSubmitNotes ? (
          <>
            <OrangeTextButton
              text="Cancel"
              onClick={handleCancel}
              disabled={false}
            />
            <OrangeTextButton
              text="Submit Notes"
              onClick={handleSubmitNotes}
              disabled={isSubmitting}
            />
          </>
        ) : (
          <OrangeTextButton onClick={handleClose} text="Ok" disabled={false} />
        )}
      </DialogActions>
    </Dialog>
  );
};
